import { defineStore } from 'pinia'
import axiosOverlayConnector from '@/services/axiosOverlayConnector.js'
import { jwtDecode } from 'jwt-decode'

export default defineStore('user', {
  state: () => ({
    loading: 0,
    token: null,
    refreshToken: null,
    compteInfo: null
  }),

  persist: {
    pick: ['token', 'refreshToken'],
    storage: localStorage
  },

  getters: {
    isLoading: (state) => {
      return state.loading > 0
    },

    isLogged() {
      if (this.token) {
        const payload = this.decodedPayloadToken
        if (!payload) return false

        return payload.exp >= Math.floor(Date.now() / 1000)
      }
      return false
    },
    decodedPayloadToken() {
      if (!this.token) return null
      return jwtDecode(this.token)
    },
    isRefreshTokenValid() {
      if (!this.refreshToken) return false
      return true
    }
  },

  actions: {
    getCompteInfo() {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/compte'

        const config = {
          url: url,
          method: 'GET'
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.compteInfo = response.data
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    changePassword(currentPassword, newPassword) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/change-password'

        const config = {
          url: url,
          method: 'POST',
          data: {
            currentPassword: currentPassword,
            newPassword: newPassword
          }
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            resolve(response.data.success)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    login(data) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/login'

        const config = {
          url: url,
          method: 'POST',
          data: data
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.token = response.data.tokens.accessToken
            this.refreshToken = response.data.tokens.refreshToken
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    register(data) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/register'

        const config = {
          url: url,
          method: 'POST',
          data: data
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            resolve(response.data.success)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    verifyEmail(data) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/verify-email'

        const config = {
          url: url,
          method: 'POST',
          data: data
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.token = response.data.tokens.accessToken
            this.refreshToken = response.data.tokens.refreshToken

            resolve(response.data.success)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    resendVerificationCode(email) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/resend-verification'

        const config = {
          url: url,
          method: 'POST',
          data: { email: email }
        }

        return axiosOverlayConnector(config)
          .then(() => {
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    forgotPassword(email) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/forgot-password'

        const config = {
          url: url,
          method: 'POST',
          data: { email: email }
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            resolve(response.data.success)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    verifyResetCode(data) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/verify-reset-code'

        const config = {
          url: url,
          method: 'POST',
          data: data
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            resolve(response.data.success)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    resetPassword(data) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/reset-password'

        const config = {
          url: url,
          method: 'POST',
          data: data
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            resolve(response.data.success)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    logout(allDevice = false) {
      if (!this.isLogged && !this.refreshToken) return Promise.resolve(true)

      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/logout'

        const config = {
          url: url,
          method: 'POST',
          data: {
            logoutAllDevices: allDevice,
            refreshToken: this.refreshToken
          }
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.token = null
            this.refreshToken = null
            resolve(response.data.success)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    deleteAccount() {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/compte'

        const config = {
          url: url,
          method: 'DELETE'
        }

        return axiosOverlayConnector(config)
          .then(() => {
            this.token = null
            this.refreshToken = null
            this.compteInfo = null
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    askNewToken(refreshToken) {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/auth/refresh-token'

        const config = {
          url: url,
          method: 'POST',
          data: {
            refreshToken: refreshToken
          }
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.token = response.data.accessToken
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    }
  }
})
